import { useCallback } from 'react';
import type { FilterTree } from '~/_shared/types/filterTree.types';
import type { MapId } from '~/_shared/types/map';
import type { SpreadsheetId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { useAjaxCall } from '~/_shared/utils/hooks/useAjaxCall';
import { notUndefined } from '~/_shared/utils/typeGuards';
import type {
  GlobalMetricValue, MetricValue,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';
import {
  OPTIMIZED_TERRITORY_AP_IMPORTANCE_MIN,
  OPTIMIZED_TERRITORY_DEFAULT_TARGET_TERRITORY_COUNT, OPTIMIZED_TERRITORY_TOPOLOGICAL_WEIGHT_MIN,
  OPTIMIZED_TERRITORY_WEIGHT_MIN,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createOptimizedTerritoriesModal/createOptimizedTerritoriesModal.container';
import {
  createOptimizedTerritories,
  type CreateOptimizedTerritoriesRequest,
  type OptimizedTerritoryAggMetric, type OptimizedTerritoryAnchorMap, type OptimizedTerritoryBtgSettings,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createOptimizedTerritoriesModal/optimizedTerritories.repository';
import type {
  BoundaryGroupId, BoundaryId, BoundaryTerritoryGroupId,
} from '~/store/boundaries/boundaryIdentifier.type';

type CreateOptimizedTerritoriesRequestParams = {
  mapId: MapId;
  clientId: number;
  spreadsheetId: SpreadsheetId;
  boundaryGroupId: BoundaryGroupId;
  groupName: string;

  iterations: number;
  dataMetricsValues: readonly MetricValue[];
  demographicsValues: readonly MetricValue[];
  globalMetrics: readonly GlobalMetricValue[];
  useConstraints: boolean;

  anchorPointsImportance?: number;
  anchorsMap?: OptimizedTerritoryAnchorMap;
  blockSubset?: readonly BoundaryId[];
  btgSettings?: OptimizedTerritoryBtgSettings;
  excludeDisconnectedBlocks?: boolean;
  exportConfig?: boolean;
  previousTerritoriesBoundaryGroupId?: BoundaryTerritoryGroupId;
  targetTerritoryCount?: Maybe<number>;
  filter?: { filter_tree: FilterTree };
};

export const useCreateOptimizedTerritories = () => {
  const { invokeAjax, isLoading } = useAjaxCall((clientId: number, params: CreateOptimizedTerritoriesRequest) =>
    createOptimizedTerritories(clientId, params)
  );

  const createOptimizedTerritoriesRequest = useCallback(({
    anchorPointsImportance,
    anchorsMap,
    blockSubset,
    boundaryGroupId,
    btgSettings,
    clientId,
    dataMetricsValues,
    demographicsValues,
    excludeDisconnectedBlocks,
    exportConfig,
    globalMetrics,
    iterations,
    mapId,
    previousTerritoriesBoundaryGroupId,
    spreadsheetId,
    targetTerritoryCount,
    groupName,
    filter,
    useConstraints,
  }: CreateOptimizedTerritoriesRequestParams
  ) => {
    const metrics = dataMetricsValues.concat(demographicsValues).filter(metric => metric.checked);

    const aggMetrics: OptimizedTerritoryAggMetric[] = metrics.map(metric => ({
      name: 'balance',
      attribute: metric.id,
      weight: Math.max(metric.weight, OPTIMIZED_TERRITORY_WEIGHT_MIN),
    }));

    const constraints = useConstraints ? metrics.filter(metric => metric.isConstraint).map(metric => ({
      attribute: metric.id,
      lower_bound: metric.constraintLowerBound ?? 0,
      upper_bound: metric.constraintUpperBound ?? Number.MAX_SAFE_INTEGER,
    })) : undefined;

    return invokeAjax(clientId, {
      map_id: mapId,
      spreadsheet_id: spreadsheetId,
      boundary_group_id: boundaryGroupId,
      agg_metrics: aggMetrics,
      global_metrics: globalMetrics.map(metric => {
        const name = metric.id;
        return {
          name,
          weight: Math.max(metric.weight, OPTIMIZED_TERRITORY_TOPOLOGICAL_WEIGHT_MIN),
        };
      }),
      iterations,
      target_territory_count: (!!anchorsMap || constraints?.length) ? undefined : targetTerritoryCount ?? OPTIMIZED_TERRITORY_DEFAULT_TARGET_TERRITORY_COUNT,
      ap_importance: (!!anchorsMap && notUndefined(anchorPointsImportance)) ? Math.max(anchorPointsImportance, OPTIMIZED_TERRITORY_AP_IMPORTANCE_MIN) : undefined,
      exclude_disconnected_blocks: excludeDisconnectedBlocks,
      block_subset: blockSubset,
      constraints,
      anchors_map: anchorsMap ? {
        map_id: anchorsMap.mapId,
        name_column: anchorsMap.nameColumnId,
      } : undefined,
      previous_territories_boundary_group_id: previousTerritoriesBoundaryGroupId,
      btg_settings: btgSettings,
      export_config: exportConfig ?? false,
      display_name: groupName,
      filter,
    });
  }, [invokeAjax]);

  return {
    isLoading,
    createOptimizedTerritoriesRequest,
  };
};
